/* ============================================================
   contact-sourcing-capture.js — /pages/contact submit, EVERY topic.

   WHAT IT DOES (since 2026-10-09)
   The contact page's submission goes to OUR endpoint instead of Shopify.
   This script intercepts the submit of the `{% form 'contact' %}` wrapper,
   POSTs the fields to /api/submit-part-request (the request-a-part endpoint),
   and reveals the on-page success panel on a 200. The endpoint saves a row
   (topic "sourcing" -> part_requests, every other topic -> contact_submissions)
   and emails PART_REQUEST_EMAIL_TO. Shopify never sees the submission, so that
   email is the only one.

   HISTORY, because the file name is now a misnomer: 2026-09-03 it was a
   sendBeacon MIRROR of topic "sourcing" fired alongside Shopify's own submit;
   2026-10-03 (GH #2563) every topic. Both sent `contact-form-<topic>` sources,
   which the server still accepts and still keeps silent (Shopify emailed
   those). The name is kept so the theme push carries no new asset and leaves
   no orphan on the live theme, and because GH #776's App Proxy migration
   enumerates this file by name.

   THE RULES THIS OBEYS, worst-consequence first

   1. A SUBMISSION IS NEVER EATEN. The native Shopify submit is the fallback,
      and it runs whenever this script cannot take the submission cleanly:
        - JS off, no fetch, no config element, an unknown topic, or a throw
          before the request starts: the submit event is NOT prevented.
        - our endpoint answers 5xx: the form is handed to the native submit.
      Either way Shopify emails the shop, exactly as before 2026-10-09.
   2. ONE SUBMISSION, ONE EMAIL. The fallback fires ONLY on a 5xx, and every
      5xx the handler can return comes before its email (the ordering contract
      in api/src/utils/part-request-handler.js). A network failure, a timeout,
      a 4xx or a 429 shows an inline error and a retry instead: a request that
      may already have reached us must not also be sent to Shopify. A 5xx
      the browser can READ is one the handler produced: a platform-level
      error page carries no Access-Control-Allow-Origin, so fetch rejects on
      it and it takes the network-failure branch, not the fallback.
   3. THE SAME PROTECTIONS as request-a-part: the honeypot (`contact[website]`)
      is forwarded verbatim as `company` so the server silently drops bots, and
      the per-IP cap applies because it is the same endpoint. Browser
      constraint validation (required, type=email, the order-number toggle in
      contact-conditional.js) runs before the submit event fires, so it is
      untouched; the server re-validates regardless.
   4. NO DOUBLE SUBMIT. The button is disabled while a request is in flight
      and further submits are swallowed until it resolves.

   TRANSPORT: fetch with a text/plain body, so it stays a CORS "simple
   request" and the browser fires no OPTIONS preflight — Azure SWA answers
   /api/* preflights with a bare 204 carrying no Access-Control-Allow-Origin
   (GH #776, docs/260619_part-request-cors-app-proxy-decision.md). Same
   constraint request-a-part.js is under.

   Loaded only at /pages/contact, via main-page-contact.liquid.
   ============================================================ */
(function () {
  'use strict';

  // contact[topic] value -> the `source` the server routes AND notifies on.
  // Must equal CONTACT_PAGE_SOURCES in api/src/utils/part-request-intake.js
  // (this file is browser JS and cannot import it); every <option value> on the
  // section's <select name="contact[topic]"> must have an entry.
  // tests/unit/contact-sourcing-capture.test.js asserts all three agree, by
  // driving this file, rather than trusting the spellings to.
  var TOPIC_SOURCES = {
    sourcing: 'contact-page-sourcing',
    fitment: 'contact-page-fitment',
    order: 'contact-page-order',
    wholesale: 'contact-page-wholesale',
    other: 'contact-page-other'
  };

  // The one topic that is a part request. Its payload keeps the part-request
  // shape (`part` <- the message), because it feeds validateSubmission().
  var SOURCING_TOPIC = 'sourcing';

  var REQUEST_TIMEOUT_MS = 20000;

  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function fieldValue(form, name) {
    var el = form.querySelector('[name="' + name + '"]');
    if (!el || typeof el.value !== 'string') return '';
    return el.value.trim();
  }

  // Maps the contact form onto the endpoint's contract. Two shapes, because
  // the server validates two shapes:
  //
  // sourcing -> validateSubmission(): `part` <- contact[body]. The contact form
  //   has no "what part" field, so the customer's message IS the request.
  // any other topic -> validateContactSubmission(): `message` <- contact[body],
  //   plus the order number the "order" topic asks for.
  //
  // Every field is read regardless of topic; a hidden conditional block keeps
  // whatever the customer typed in it, and the row records what they sent.
  function buildPayload(form, topic) {
    var payload = {
      partnumber: fieldValue(form, 'contact[partnumber]'),
      year: fieldValue(form, 'contact[year]'),
      make: fieldValue(form, 'contact[make]'),
      model: fieldValue(form, 'contact[model]'),
      email: fieldValue(form, 'contact[email]'),
      name: fieldValue(form, 'contact[name]'),
      company: fieldValue(form, 'contact[website]'), // honeypot, forwarded verbatim
      source: TOPIC_SOURCES[topic]
    };
    if (topic === SOURCING_TOPIC) {
      payload.part = fieldValue(form, 'contact[body]');
    } else {
      payload.message = fieldValue(form, 'contact[body]');
      payload.order_number = fieldValue(form, 'contact[order_number]');
    }
    return payload;
  }

  function messageOf(payload) {
    return payload.part !== undefined ? payload.part : payload.message;
  }

  // 400 bodies carry the server's validation strings, which name columns
  // ("requested_part is required"). Translate the one a customer can actually
  // hit past browser validation — an address type=email accepts but the
  // server's stricter pattern refuses — and keep the rest generic.
  function messageFor400(body) {
    var errors = (body && body.errors) || [];
    for (var i = 0; i < errors.length; i++) {
      if (/email/i.test(String(errors[i]))) {
        return 'That email address doesn’t look right — check it and send again.';
      }
    }
    return 'Check the form and send it again.';
  }

  function init() {
    var cfg = document.querySelector('[data-part-request-capture]');
    if (!cfg) return;
    var form = cfg.closest('form');
    var endpoint = cfg.getAttribute('data-endpoint');
    if (!form || !endpoint) return;
    // No fetch: register nothing, so the native Shopify submit is the path.
    if (typeof window.fetch !== 'function') return;

    var successEl = form.querySelector('[data-contact-success]');
    var fieldsEl = form.querySelector('[data-contact-fields]');
    var errorEl = form.querySelector('[data-contact-error]');
    var errorMsgEl = errorEl ? errorEl.querySelector('[data-contact-error-msg]') : null;
    var submitBtn = form.querySelector('[type="submit"]');
    var submitLabel = submitBtn ? submitBtn.textContent : '';
    var inFlight = false;

    function lockSubmit() {
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }
    }
    function unlockSubmit() {
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = submitLabel; }
    }
    function hideError() {
      if (errorEl) errorEl.hidden = true;
    }
    function showError(msg) {
      inFlight = false;
      unlockSubmit();
      if (!errorEl) return;
      if (errorMsgEl) errorMsgEl.textContent = msg;
      errorEl.hidden = false;
      if (typeof errorEl.scrollIntoView === 'function') {
        errorEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
    function showSuccess() {
      if (fieldsEl) fieldsEl.hidden = true;
      if (successEl) {
        successEl.hidden = false;
        var panel = successEl.querySelector('[data-contact-success-panel]');
        if (panel && typeof panel.focus === 'function') panel.focus();
        if (typeof successEl.scrollIntoView === 'function') {
          successEl.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
      // Button intentionally stays disabled — the form is gone.
    }
    // Hand the submission to Shopify. form.submit() fires no submit event, so
    // this listener is not re-entered, and it skips constraint validation,
    // which already passed for this submission.
    function nativeSubmit() {
      form.submit();
    }

    form.addEventListener('submit', function (e) {
      if (inFlight) { e.preventDefault(); return; }

      var payload;
      try {
        var topic = fieldValue(form, 'contact[topic]');
        // An unmapped topic (none today; the test enumerates the live
        // <select>) goes to Shopify rather than guessing a table.
        if (!Object.prototype.hasOwnProperty.call(TOPIC_SOURCES, topic)) return;
        payload = buildPayload(form, topic);
      } catch (err) {
        return; // not prevented: the native submit proceeds
      }

      e.preventDefault();
      hideError();

      // Browser validation normally makes these unreachable; the server would
      // 400 them anyway, so say so here rather than spend a request.
      if (!messageOf(payload)) { showError('Add a message so Hubes knows what you need.'); return; }
      if (!EMAIL_RE.test(payload.email)) { showError('Add an email so Hubes can reply.'); return; }

      inFlight = true;
      lockSubmit();

      var controller = typeof window.AbortController === 'function' ? new window.AbortController() : null;
      var timer = controller ? setTimeout(function () { controller.abort(); }, REQUEST_TIMEOUT_MS) : null;

      var opts = {
        method: 'POST',
        headers: { 'Content-Type': 'text/plain' },
        body: JSON.stringify(payload)
      };
      if (controller) opts.signal = controller.signal;

      var request;
      try {
        request = window.fetch(endpoint, opts);
      } catch (err) {
        // fetch threw synchronously: the request never left, so Shopify can
        // take it without any chance of a second email.
        if (timer) clearTimeout(timer);
        nativeSubmit();
        return;
      }

      request
        .then(function (res) {
          return res.json().catch(function () { return {}; }).then(function (body) {
            return { ok: res.ok, status: res.status, body: body };
          });
        })
        .then(function (r) {
          if (timer) clearTimeout(timer);
          if (r.ok) {
            showSuccess();
          } else if (r.status >= 500) {
            // Our side failed BEFORE emailing (handler ordering contract), so
            // Shopify's email is the first and only one.
            nativeSubmit();
          } else if (r.status === 429) {
            showError('That’s a lot of messages in a short window — give it a few minutes and try again.');
          } else {
            showError(messageFor400(r.body));
          }
        })
        .catch(function () {
          if (timer) clearTimeout(timer);
          // Ambiguous: the request may have reached us. Retry is the customer's
          // call; sending it to Shopify too could be a second email.
          showError('Couldn’t reach us just now — try again, or call or email the shop (details below).');
        });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
